<template>
  <transition name="confirm-fade">
    <div
      v-if="store.show"
      class="fixed inset-0 z-[9998] flex items-center justify-center bg-cyma-purple/20 p-4 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
      @click.self="cancel"
    >
      <div
        ref="panel"
        class="w-full max-w-md rounded-cyma bg-white/95 p-6 ring-1 ring-white/50 shadow-[0_24px_60px_rgba(76,23,96,0.25)] backdrop-blur-xl"
      >
        <div class="flex items-start gap-4">
          <span
            class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-cyma-md shadow-cyma-inset"
            :class="
              store.tone === 'danger'
                ? 'bg-cyma-red text-cyma-red-title'
                : 'bg-cyma-purple text-white'
            "
          >
            <Icon
              :icon="
                store.tone === 'danger'
                  ? 'heroicons:exclamation-triangle'
                  : 'heroicons:question-mark-circle'
              "
              class="h-6 w-6"
              aria-hidden="true"
            />
          </span>
          <div class="min-w-0 flex-1">
            <h2
              id="confirm-dialog-title"
              class="text-lg font-bold text-cyma-gray"
            >
              {{ store.title }}
            </h2>
            <p
              id="confirm-dialog-message"
              class="mt-1 text-sm text-cyma-gray/70"
            >
              {{ store.message }}
            </p>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-3">
          <button
            ref="cancelButton"
            type="button"
            class="rounded-cyma-pill bg-white px-5 py-2 text-sm font-medium text-cyma-gray shadow-cyma-inset transition hover:text-cyma-purple"
            @click="cancel"
          >
            {{ store.cancelText }}
          </button>
          <button
            ref="confirmButton"
            type="button"
            class="rounded-cyma-pill px-5 py-2 text-sm font-semibold text-white shadow-cyma-inset transition hover:opacity-90"
            :class="store.tone === 'danger' ? 'bg-red-500' : 'bg-cyma-purple'"
            @click="accept"
          >
            {{ store.confirmText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, nextTick, ref } from "vue";
import { Icon } from "@iconify/vue";
import { useConfirmStore } from "@/stores/confirm.store";

defineOptions({ name: "ConfirmDialog" });

const store = useConfirmStore();

const panel = ref<HTMLElement | null>(null);
const cancelButton = ref<HTMLButtonElement | null>(null);
const confirmButton = ref<HTMLButtonElement | null>(null);
// Elemento que tenía el foco antes de abrir, para restaurarlo al cerrar.
let lastFocused: HTMLElement | null = null;

const accept = () => store.settle(true);
const cancel = () => store.settle(false);

// Elementos enfocables dentro del panel (los dos botones hoy, pero query genérico
// para que siga funcionando si se agregan controles).
const focusables = (): HTMLElement[] =>
  panel.value
    ? Array.from(
        panel.value.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled"))
    : [];

// Trap de Tab: el foco no puede salir del diálogo mientras está abierto.
const trapTab = (e: KeyboardEvent) => {
  const items = focusables();
  if (items.length === 0) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement as HTMLElement | null;
  const inside = !!active && !!panel.value?.contains(active);

  if (e.shiftKey) {
    if (!inside || active === first) {
      e.preventDefault();
      last.focus();
    }
  } else if (!inside || active === last) {
    e.preventDefault();
    first.focus();
  }
};

const onKeydown = (e: KeyboardEvent) => {
  if (!store.show) return;
  if (e.key === "Escape") cancel();
  else if (e.key === "Tab") trapTab(e);
};

// Al abrir: guarda el foco previo y mueve el foco dentro del diálogo. En tono
// `danger` enfoca Cancelar (acción segura por defecto, evita confirmar un borrado
// con Enter por accidente); en el resto, Confirmar. Al cerrar: restaura el foco.
watch(
  () => store.show,
  (open) => {
    if (open) {
      lastFocused = document.activeElement as HTMLElement | null;
      nextTick(() => {
        const target =
          store.tone === "danger" ? cancelButton.value : confirmButton.value;
        target?.focus();
      });
    } else if (lastFocused) {
      lastFocused.focus?.();
      lastFocused = null;
    }
  }
);

onMounted(() => document.addEventListener("keydown", onKeydown));
onUnmounted(() => document.removeEventListener("keydown", onKeydown));
</script>

<style scoped>
.confirm-fade-enter-active,
.confirm-fade-leave-active {
  transition: opacity 0.2s ease;
}
.confirm-fade-enter-from,
.confirm-fade-leave-to {
  opacity: 0;
}
</style>
